import { useApi } from '@classgo/app-kit';
import type { AlbertQuotaView } from '@classgo/shared';
import { Badge, Card, EmptyState, Select, Spinner, Tabs } from '@classgo/ui';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import { listQuery } from '../../academic/api';
import { albertQueries } from '../../albert/api';
import type { AlbertPair } from '../../albert/types';
import { curriculumQueries } from '../../curriculum/api';
import { useT } from '../../i18n';
import { can } from '../../shell';
import { useTenant } from '../../tenant/tenant-provider';
import { DraftPanel } from './draft-panel';
import { MaterialPanel } from './material-panel';
import { PlanPanel } from './plan-panel';
import { pairKey, pairsOf, topicsOf } from './support';

/**
 * The plan and material tabs without a topic: the plan has none yet, or one is still to pick.
 */
const NoTopic = ({ ready }: { ready: boolean }) => {
  const t = useT();
  return (
    <EmptyState
      title={t('albert.pickTopic.title')}
      description={ready ? t('albert.noTopics') : t('albert.pickTopic.body')}
    />
  );
};

/**
 * The teacher's Albert workspace (#118): pick one of their classes (`GET /me/classes`) and a
 * topic of its plan de estudios, then plan the lesson, generate a document or draft a text in
 * three tabs. The quota of the month shows as "N de M este mes" from the topic's plan list and
 * after every answer. Someone with the `albert:use` key and no classes picks a group and a
 * subject of the school instead.
 */
export const AlbertScreen = () => {
  const t = useT();
  const api = useApi();
  const { session } = useTenant();
  const holdsKey = can(session, 'albert:use');
  const classes = useQuery(albertQueries.classes(api));
  const pairs = useMemo(() => pairsOf(classes.data), [classes.data]);
  const composing = holdsKey && classes.isSuccess && pairs.length === 0;
  const groups = useQuery({ ...listQuery(api, 'groups'), enabled: composing });
  const subjects = useQuery({ ...listQuery(api, 'subjects'), enabled: composing });
  const grades = useQuery({ ...listQuery(api, 'grades'), enabled: composing });
  const [selectedPair, setSelectedPair] = useState('');
  const [groupId, setGroupId] = useState('');
  const [subjectId, setSubjectId] = useState('');
  const [topicId, setTopicId] = useState('');
  const [quota, setQuota] = useState<AlbertQuotaView | null>(null);

  useEffect(() => {
    if (!selectedPair && pairs[0]) setSelectedPair(pairKey(pairs[0]));
  }, [selectedPair, pairs]);

  const pair = useMemo<AlbertPair | null>(() => {
    const own = pairs.find((candidate) => pairKey(candidate) === selectedPair);
    if (own) return own;
    if (!composing) return null;
    const group = groups.data?.items.find((row) => row.id === groupId);
    const subject = subjects.data?.items.find((row) => row.id === subjectId);
    if (!group || !subject) return null;
    return {
      groupId: group.id,
      groupName: group.name,
      gradeId: group.gradeId,
      gradeName: grades.data?.items.find((row) => row.id === group.gradeId)?.name ?? '',
      subjectId: subject.id,
      subjectName: subject.name,
    };
  }, [pairs, selectedPair, composing, groups.data, subjects.data, grades.data, groupId, subjectId]);

  const plan = useQuery(curriculumQueries.plan(api, pair?.gradeId ?? '', pair?.subjectId ?? ''));
  const topics = useMemo(() => topicsOf(plan.data), [plan.data]);
  const topic = topics.find((candidate) => candidate.id === topicId) ?? null;

  useEffect(() => {
    if (!topic && topics[0]) setTopicId(topics[0].id);
  }, [topic, topics]);

  const plans = useQuery(albertQueries.plans(api, topic?.id ?? ''));
  useEffect(() => {
    if (plans.data) setQuota(plans.data.quota);
  }, [plans.data]);

  const changePair = (next: string) => {
    setSelectedPair(next);
    setTopicId('');
  };

  if (classes.isPending) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }
  if (!composing && pairs.length === 0) {
    return (
      <EmptyState title={t('albert.noClasses.title')} description={t('albert.noClasses.body')} />
    );
  }

  return (
    <div className="grid gap-4 lg:grid-cols-[20rem_1fr]">
      <Card title={t('albert.context.title')}>
        <p className="mb-3 text-xs text-gray-500">{t('albert.intro')}</p>
        <div className="space-y-3">
          {composing ? (
            <>
              <Select
                label={t('albert.field.group')}
                value={groupId}
                onChange={(event) => {
                  setGroupId(event.target.value);
                  setTopicId('');
                }}
                placeholder={t('albert.field.group')}
                options={(groups.data?.items ?? []).map((row) => ({
                  value: row.id,
                  label: row.name,
                }))}
              />
              <Select
                label={t('albert.field.subject')}
                value={subjectId}
                onChange={(event) => {
                  setSubjectId(event.target.value);
                  setTopicId('');
                }}
                placeholder={t('albert.field.subject')}
                options={(subjects.data?.items ?? []).map((row) => ({
                  value: row.id,
                  label: row.name,
                }))}
              />
            </>
          ) : (
            <Select
              label={t('albert.field.pair')}
              value={selectedPair}
              onChange={(event) => changePair(event.target.value)}
              options={pairs.map((candidate) => ({
                value: pairKey(candidate),
                label: `${candidate.groupName} · ${candidate.subjectName}`,
              }))}
            />
          )}
          <Select
            label={t('albert.field.topic')}
            value={topic?.id ?? ''}
            onChange={(event) => setTopicId(event.target.value)}
            options={topics.map((row) => ({
              value: row.id,
              label: `${row.periodName} · ${row.title}`,
            }))}
            disabled={!pair || topics.length === 0}
            {...(pair && plan.isSuccess && topics.length === 0
              ? { placeholder: t('albert.noTopics') }
              : {})}
          />
          {topic && topic.logros.length > 0 ? (
            <ul className="list-disc space-y-1 pl-4 text-xs text-gray-600">
              {topic.logros.map((logro) => (
                <li key={logro}>{logro}</li>
              ))}
            </ul>
          ) : null}
          <div className="flex items-center gap-2 border-t border-gray-100 pt-3 text-sm">
            <span className="text-gray-600">{t('albert.quota.label')}</span>
            {quota ? (
              <Badge tone={quota.remaining > 0 ? 'brand' : 'danger'}>
                {t('albert.quota', { used: quota.used, monthly: quota.monthly })}
              </Badge>
            ) : (
              <span className="text-xs text-gray-500">{t('albert.quota.unknown')}</span>
            )}
          </div>
        </div>
      </Card>
      <Card
        title={
          pair
            ? `${pair.groupName} · ${pair.subjectName}${topic ? ` · ${topic.title}` : ''}`
            : t('albert.title')
        }
      >
        {pair ? (
          <Tabs
            label={t('albert.title')}
            keepMounted
            items={[
              {
                id: 'plan',
                label: t('albert.tab.plan'),
                content: topic ? (
                  <PlanPanel
                    key={topic.id}
                    pair={pair}
                    topic={topic}
                    plan={plans.data?.items[0] ?? null}
                    loading={plans.isPending}
                    onQuota={setQuota}
                  />
                ) : (
                  <NoTopic ready={plan.isSuccess} />
                ),
              },
              {
                id: 'material',
                label: t('albert.tab.material'),
                content: topic ? (
                  <MaterialPanel key={topic.id} pair={pair} topic={topic} onQuota={setQuota} />
                ) : (
                  <NoTopic ready={plan.isSuccess} />
                ),
              },
              {
                id: 'draft',
                label: t('albert.tab.draft'),
                content: <DraftPanel key={pairKey(pair)} pair={pair} onQuota={setQuota} />,
              },
            ]}
          />
        ) : (
          <EmptyState
            title={t('albert.pickTopic.title')}
            description={t('albert.pickTopic.body')}
          />
        )}
      </Card>
    </div>
  );
};
